import { flip, size } from "@floating-ui/react";
import { ClickAwayListener } from "@mui/base";
import { Unstable_Popup as BasePopup } from "@mui/base/Unstable_Popup";
import { FilterList } from "@mui/icons-material";
import { Box, Button, Checkbox, Link, Sheet, Stack, ToggleButtonGroup, Typography } from "@mui/joy";
import { Column, Table } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useShallow } from "zustand/react/shallow";

import { Route } from "../../core/types";
import { useGradeSystemChoices, useVenueGradeSystem } from "../../hooks/grades";
import ChoiceRangeSlider from "../forms/ChoicesSlider";
import ColorsSelect from "../forms/ColorsSelect";
import GradeRangeSlider from "../forms/GradeRangeSlider";
import { DateInput } from "../forms/TextInput";
import { useRoutesFacets, useRoutesTableStore, useSetFilterParam } from "./RoutesProvider2";
import {
  FilterNameOfType,
  FilterParams,
  MultipleChoiceFilter,
  RangeFilter,
  RoutesFacets,
  getRangeFilterChoices,
  isFilterOfType,
  isMultipleChoiceFilter,
  isValidFilterName,
} from "./useFilteredRoutes";

type Props = {
  column: Column<Route, unknown>;
  table: Table<Route>;
};

const MultipleChoiceFilterContent = ({
  filterName,
  filterParamValue,
  facetsMap,
}: {
  filterName: FilterNameOfType<"multipleChoice">;
  filterParamValue?: MultipleChoiceFilter<string | null>;
  facetsMap?: RoutesFacets[keyof RoutesFacets];
  onClose: () => void;
}) => {
  const filterParams = useRoutesTableStore((state) => state.filterParams);
  const setFilterParam = useSetFilterParam();

  const onClickItem = (value: string | null) => {
    const isSelected = filterParamValue?.matches.has(value);

    const updatedSet = new Set(filterParamValue?.matches || []);
    if (isSelected) {
      updatedSet.delete(value);
    } else {
      updatedSet.add(value);
    }
    const newFilterParam = {
      matches: updatedSet,
    } as MultipleChoiceFilter<string | null>;
    setFilterParam(filterName, updatedSet.size ? newFilterParam : undefined);
  };

  let filterValues = facetsMap ? Array.from(facetsMap.values()) : [];
  if (filterName === "competitionCategories") {
    const circuitOrCompetitionUuids = filterParams.circuit?.matches;
    if (circuitOrCompetitionUuids?.size) {
      filterValues = Array.from(filterValues).filter((v) =>
        v.value ? circuitOrCompetitionUuids.has(v.value.split(":")[0]) : false,
      );
    }
  }

  return (
    <Box sx={{ paddingBottom: 0.5 }}>
      {filterValues.map(({ value, name, count }) => {
        return (
          <Box key={value ?? "null"}>
            <Checkbox
              checked={filterParamValue?.matches.has(value)}
              onChange={() => onClickItem(value)}
              label={`${name} (${count})`}
              sx={{ paddingX: 1, display: "flex", paddingY: 0.5 }}
              size="sm"
            />
          </Box>
        );
      })}
    </Box>
  );
};

const HoldColorsFilterContent = ({
  filterName,
  filterParamValue,
  facetsMap,
}: {
  filterName: "holdColors";
  filterParamValue?: FilterParams["holdColors"];
  facetsMap?: RoutesFacets["holdColors"];
}) => {
  const setFilterParam = useSetFilterParam();

  return (
    <Box
      sx={{
        paddingX: 1,
        paddingY: 1,
        width: 224,
      }}
    >
      <ColorsSelect
        options={facetsMap ? Array.from(facetsMap.values(), ({ value }) => value) : []}
        value={filterParamValue ? Array.from(filterParamValue.matches) : emptyArray}
        setValue={(colors) => {
          setFilterParam(filterName, {
            matches: new Set(colors),
          });
        }}
      />
    </Box>
  );
};

const emptyArray = [] as never[];

const RangeFilterContent = ({
  filterName,
  filterParamValue,
}: {
  filterName: FilterNameOfType<"range">;
  filterParamValue?: RangeFilter<number>;
  onClose: () => void;
}) => {
  console.log({ filterParamValue });
  const setFilterParam = useSetFilterParam();
  const choices = getRangeFilterChoices(filterName);

  if (!choices) {
    // should never happen
    return null;
  }

  return (
    <Box sx={{ paddingX: 2.5, paddingY: 1, width: 250 }}>
      <ChoiceRangeSlider<string | number | null>
        choices={choices}
        minValue={filterParamValue?.min}
        maxValue={filterParamValue?.max}
        valueSuffix={filterName === "age" ? "old" : "away"}
        // reverse={filterName === "age"}
        onChange={(change) => {
          setFilterParam(filterName, {
            // @ts-expect-error
            min: change.minValue ?? undefined,
            // @ts-expect-error
            max: change.maxValue ?? undefined,
          });
        }}
      />
    </Box>
  );
};

const fixedTimeRangeChoices = [
  {
    label: "Last 7d",
    value: 7,
  },
  {
    label: "Last 30d",
    value: 30,
  },
  {
    label: "Last 90d",
    value: 90,
  },
  {
    label: "Last 6m",
    value: 180,
  },
  {
    label: "Last 12m",
    value: 365,
  },
];

const DateRangeFilterContent = ({
  filterName,
  filterParamValue,
}: {
  filterName: "startTime" | "endTime";
  filterParamValue?: FilterParams["startTime"] | FilterParams["endTime"];
  onClose: () => void;
}) => {
  const setFilterParam = useSetFilterParam();
  const formMethods = useForm({
    defaultValues: {
      start: filterParamValue?.min,
      end: filterParamValue?.max,
    },
  });

  useEffect(() => {
    formMethods.reset({
      start: filterParamValue?.min,
      end: filterParamValue?.max,
    });
  }, [formMethods, filterParamValue?.min, filterParamValue?.max]);

  useEffect(() => {
    const subscription = formMethods.watch((value, { type }) => {
      if (type === "change") {
        setFilterParam(filterName, {
          min: value.start,
          max: value.end,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [formMethods, filterName, setFilterParam]);

  const now = new Date();

  const fixedChoiceCurrentValue = useMemo(() => {
    const now = new Date();
    if (!filterParamValue) {
      return undefined;
    }
    if (!filterParamValue.min || filterParamValue.max) {
      return -1;
    }
    const daysAgo = Math.round(
      (now.getTime() - new Date(filterParamValue.min).getTime()) / (24 * 60 * 60 * 1000),
    );
    return fixedTimeRangeChoices.find((choice) => choice.value === daysAgo)?.value ?? -1;
  }, [filterParamValue]);

  return (
    <FormProvider {...formMethods}>
      <ToggleButtonGroup
        sx={{
          width: "480px",
          flexWrap: "wrap",
          paddingX: 1.5,
          marginBottom: 0.5,
          marginTop: 0.75,
          ".MuiButton-root": {
            whiteSpace: "nowrap",
          },
        }}
        variant="soft"
        buttonFlex={1}
        value={JSON.stringify(fixedChoiceCurrentValue)}
        onChange={(_, value) => {
          const val = JSON.parse(value ?? "null");
          if (val) {
            setFilterParam(filterName, {
              min: new Date(now.getTime() - val * 24 * 60 * 60 * 1000).toISOString(),
              max: undefined,
            });
          }
        }}
      >
        {fixedTimeRangeChoices.map(({ label, value }) => {
          const isSelected = value === fixedChoiceCurrentValue;
          return (
            <Button
              value={JSON.stringify(value)}
              key={value}
              color={isSelected ? "primary" : "neutral"}
              variant={isSelected ? "solid" : "soft"}
            >
              {label}
            </Button>
          );
        })}
      </ToggleButtonGroup>
      <Stack
        sx={{ paddingX: 1.5, paddingBottom: 1.5 }}
        direction="row"
        alignItems="baseline"
        spacing={1}
      >
        <Typography>Between</Typography>
        <Box sx={{ width: 170 }}>
          <DateInput label={undefined} name="start" withTime={false} asDateTime maxDate={"TODAY"} />
        </Box>
        <Typography>and</Typography>
        <Box sx={{ width: 170 }}>
          <DateInput
            label={undefined}
            name="end"
            withTime={false}
            asDateTime
            minDate={filterParamValue?.min}
            maxDate={"TODAY"}
          />
        </Box>
      </Stack>
    </FormProvider>
  );
};

const GradeFilterContent = ({
  filterName,
  filterParamValue,
}: {
  filterName: "grade";
  filterParamValue?: FilterParams["grade"];
  onClose: () => void;
}) => {
  const setFilterParam = useSetFilterParam();
  const defaultGradeSystem = useVenueGradeSystem();
  const gradeSystemChoices = useGradeSystemChoices();

  return (
    <Box sx={{ paddingX: 2.5, paddingY: 1, width: 250 }}>
      <GradeRangeSlider
        gradeSystem={filterParamValue?.system || defaultGradeSystem}
        gradeSystemChoices={gradeSystemChoices}
        minGradeValue={filterParamValue?.min}
        maxGradeValue={filterParamValue?.max}
        onChange={(change) => {
          setFilterParam(filterName, {
            system: change.gradeSystem,
            min: change.minGradeValue || undefined,
            max: change.maxGradeValue || undefined,
          });
        }}
      />
    </Box>
  );
};

export default function FilterPopup({ column }: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  // const { filterParams, routesFacets, removeFilterParam } = useRoutesUi();
  const { filterParams, removeFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      removeFilterParam: state.removeFilterParam,
    })),
  );
  const routesFacets = useRoutesFacets();

  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const filterName = (column.columnDef.meta?.["filterName"] as string | undefined) || column.id;

  if (!isValidFilterName(filterName)) {
    return null;
  }
  const filterParamValue = filterParams[filterName];
  const facetsMap =
    routesFacets && filterName in routesFacets
      ? routesFacets[filterName as keyof RoutesFacets]
      : undefined;

  const onClose = () => setOpen(false);
  let filterContent;
  if (filterName === "holdColors") {
    filterContent = (
      <HoldColorsFilterContent
        filterName={filterName}
        filterParamValue={filterParams.holdColors}
        facetsMap={routesFacets?.holdColors}
      />
    );
  } else if (isFilterOfType(filterName, "multipleChoice")) {
    filterContent = (
      <MultipleChoiceFilterContent
        filterName={filterName}
        filterParamValue={filterParams[filterName]}
        facetsMap={facetsMap}
        onClose={onClose}
      />
    );
  } else if (isFilterOfType(filterName, "grade")) {
    filterContent = (
      <GradeFilterContent
        filterName={filterName}
        filterParamValue={filterParams[filterName]}
        onClose={onClose}
      />
    );
  } else if (isFilterOfType(filterName, "range")) {
    filterContent = (
      <RangeFilterContent
        filterName={filterName}
        filterParamValue={filterParams[filterName]}
        onClose={onClose}
      />
    );
  } else if (isFilterOfType(filterName, "dateRange")) {
    filterContent = (
      <DateRangeFilterContent
        filterName={filterName}
        filterParamValue={filterParams[filterName]}
        onClose={onClose}
      />
    );
  } else {
    return null;
  }

  return (
    <>
      <Button
        variant="plain"
        color={filterParamValue !== undefined || open ? "primary" : "neutral"}
        sx={{
          paddingX: 1,
          marginRight: "-2px",
          "&:active, &:hover": {
            backgroundColor: "transparent !important",
          },
          "&:hover .MuiSvgIcon-root": {
            color: "primary.500",
          },
        }}
        size="sm"
        ref={buttonRef}
        onClick={() => setOpen(!open)}
      >
        <Typography level="body-xs" textColor="primary.500" sx={{ marginRight: 0.25 }}>
          {isMultipleChoiceFilter(filterParamValue) ? filterParamValue.matches.size : null}
        </Typography>
        <FilterList sx={{ fontSize: "14px" }} />
      </Button>
      <BasePopup
        anchor={buttonRef.current}
        open={open}
        placement="bottom-end"
        middleware={[
          flip(),
          size({
            apply({ availableHeight }) {
              setMaxHeight(availableHeight - 8);
            },
          }),
        ]}
        style={{
          zIndex: 100,
        }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Sheet
            variant="outlined"
            sx={{
              boxShadow: "sm",
              borderRadius: "md",
              display: "flex",
              flexDirection: "column",
              maxHeight: maxHeight,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                paddingX: 1,
                paddingTop: 0.5,
                justifyContent: "space-between",
              }}
            >
              <Typography fontSize="sm" fontWeight="bold">
                Select values...
              </Typography>
              {filterParamValue !== undefined ? (
                <Link
                  onClick={() => {
                    removeFilterParam(filterName);
                    setOpen(false);
                  }}
                  fontSize="xs"
                  fontWeight="bold"
                >
                  Clear
                </Link>
              ) : null}
            </Box>
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              {filterContent}
            </Box>
          </Sheet>
        </ClickAwayListener>
      </BasePopup>
      {/* </ClickAwayListener> */}
    </>
  );
}
